import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames/bind';
import i18n from 'i18n';
import { CopyableOverlayTrigger } from 'components/CopyableOverlayTrigger/CopyableOverlayTrigger';
import styles from './storeLimitationGroups.module.scss';
import {
  DeliveryTargetGroup,
  isUnknownMunicipalityGroup,
  resolveStoreName
} from './groupStoreLimitations';
import {
  ABNORMAL_CATEGORIES,
  ABNORMAL_CATEGORY_ORDER,
  AbnormalCategory,
  DeliveryTargetUnitKey,
  StatusIndex,
  statusCategory,
  statusHintKeys,
  statusTone
} from './deliveryTargetStatus';

const cx = classNames.bind(styles);

export const StoreLimitationGroups: React.FC<{
  groups: DeliveryTargetGroup[];
  /** 逐間門市的異常狀態；未提供代表後端沒回，整份清單維持中性色。 */
  statusIndex?: StatusIndex;
  unitKey: DeliveryTargetUnitKey;
}> = ({ groups, statusIndex, unitKey }) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  // 一間門市都列不出來的縣市（底下全部停用）也要能展開：那一列與其他縣市列長得一樣，
  // 少了箭頭反而像壞掉。展開之後是空的，旁邊的 0 已經把話講完。
  const expandableKeys = useMemo(() => groups.map((g) => g.key), [groups]);
  const allExpanded =
    expandableKeys.length > 0 &&
    expandableKeys.every((key) => expandedKeys.includes(key));

  const toggleGroup = useCallback((key: string) => {
    setExpandedKeys((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    );
  }, []);

  const toggleAll = useCallback(() => {
    setExpandedKeys(allExpanded ? [] : expandableKeys);
  }, [allExpanded, expandableKeys]);

  // 依 reasons[0] 分類，與統計卡同一個口徑 —— 所以一列的 pill 相加等於該縣市的
  // 異常門市數。與該列的筆數同分母：兩邊都算項目數，重複選到的同一間各算一次。
  const categoryCountsOf = (group: DeliveryTargetGroup) => {
    const counts = {} as { [category in AbnormalCategory]?: number };
    if (!statusIndex) {
      return counts;
    }
    group.targets.forEach((target) => {
      const category = statusCategory(statusIndex.get(target.value));
      if (category) {
        counts[category] = (counts[category] ?? 0) + 1;
      }
    });
    return counts;
  };

  return (
    <div className={styles.storeLimitationGroups}>
      {/* 工具列整列獨立，底下的分隔線才能跨滿外框；左右留白由它自己負責。
          沒有任何縣市展得開時不出現，免得留下一顆按了沒事的按鈕與一條孤立的分隔線。 */}
      {expandableKeys.length > 0 && (
        <div className={styles.toolbar}>
          <button
            type='button'
            className={styles.toggleAll}
            onClick={toggleAll}
          >
            {i18n.t(
              allExpanded
                ? 'limitation.labels.collapseAllStores'
                : 'limitation.labels.expandAllStores'
            )}
          </button>
        </div>
      )}
      <div className={styles.groupList}>
        {groups.map((group) => {
          const expanded = expandedKeys.includes(group.key);
          // 門市通路的縣市列沿用選項樹的寫法「臺北市分店」；機台通路沒有對應說法。
          const groupLabel =
            unitKey === 'store' && !isUnknownMunicipalityGroup(group)
              ? i18n.t('productGroup.space.attributes.store.postfix', {
                  value: group.label
                })
              : group.label;
          const categoryCounts = categoryCountsOf(group);
          return (
            <div className={styles.group} key={group.key}>
              <button
                type='button'
                className={styles.groupToggle}
                onClick={() => toggleGroup(group.key)}
                aria-expanded={expanded}
              >
                {/* pill 緊跟在縣市名後面：它講的是這個縣市的事，靠右會讓人
                    誤讀成與總數同一組數字。 */}
                <span className={styles.groupName}>
                  <FontAwesomeIcon
                    className={styles.caret}
                    icon={expanded ? faCaretDown : faCaretRight}
                  />
                  {groupLabel}
                  {/* 數量為 0 的分類整顆不出現：長期掛一顆「天氣不符 0」會讓真的
                      有門市不符時看不出差別。 */}
                  {ABNORMAL_CATEGORY_ORDER.filter(
                    (category) => (categoryCounts[category] ?? 0) > 0
                  ).map((category) => (
                    <span
                      className={cx(
                        'abnormalPill',
                        ABNORMAL_CATEGORIES[category].tone
                      )}
                      key={category}
                    >
                      {`${i18n.t(
                        ABNORMAL_CATEGORIES[category].i18nKey(unitKey)
                      )} ${categoryCounts[category]}`}
                    </span>
                  ))}
                </span>
                <span className={styles.count}>{group.count}</span>
              </button>
              {/* 用 hidden 而不是不渲染：如果未來提供搜尋功能，展開後
                節點必須真的在 DOM 裡。 */}
              <div className={styles.targets} hidden={!expanded}>
                {/* 同一個 value 可能重複出現（刻意不去重），所以 key 要帶上序號。 */}
                {group.targets.map((target, index) => {
                  const status = statusIndex?.get(target.value);
                  const tone = statusTone(status);
                  const hints = statusHintKeys(status, unitKey);
                  const storeName = resolveStoreName(
                    target,
                    status?.deliveryTargetName
                  );
                  return (
                    // 三種原因共用同一個紅色，顏色分不出是哪一種，也講不出同時
                    // 成立的第二個原因，只能靠這個浮層。可投的門市沒有原因可講，
                    // overlay 給 undefined 就不會浮出來。
                    <CopyableOverlayTrigger
                      key={`${target.value}#${index}`}
                      placement='bottom'
                      overlay={
                        hints.length > 0
                          ? hints.map((key) => i18n.t(key)).join(' , ')
                          : undefined
                      }
                    >
                      <span className={cx('target', tone)}>
                        <span className={styles.targetName}>{storeName}</span>
                        {target.storeNum && (
                          <span className={styles.targetId}>
                            {target.storeNum}
                          </span>
                        )}
                        {target.characteristic && (
                          <span className={styles.targetId}>
                            {target.characteristic}
                          </span>
                        )}
                      </span>
                    </CopyableOverlayTrigger>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
